<!--
 * @Description: 服务详情页面
 * @Features: 服务详细信息展示、预约下单、评价展示
-->
<template>
  <div class="service-detail-page">
    <div class="service-detail-container">
      <!-- 加载状态 -->
      <div v-if="loading" class="service-detail-loading-wrapper">
        <div class="service-detail-skeleton-container">
          <div
            class="service-detail-skeleton-line"
            v-for="n in 8"
            :key="n"
          ></div>
        </div>
      </div>

      <!-- 服务详情 -->
      <div v-else-if="serviceInfo" class="service-detail-content">
        <!-- 服务主要信息卡片 -->
        <div class="service-main-card">
          <div class="service-info-section">
            <h1 class="service-title">{{ serviceInfo.name }}</h1>

            <div class="service-status-tags">
              <span
                class="service-status-tag"
                :class="serviceInfo.isOnline === '1' ? 'online' : 'offline'"
              >
                {{ serviceInfo.isOnline === "1" ? "在线" : "打烊" }}
              </span>
              <span class="service-way-tag">{{
                getServiceWayText(serviceInfo.serviceWay)
              }}</span>
              <span class="order-type-tag">{{
                getOrderTypeText(serviceInfo.orderType)
              }}</span>
            </div>

            <div class="service-rating-section">
              <el-rate
                v-model="ratingValue"
                disabled
                show-score
                text-color="#ff9900"
                score-template="{value}"
                :max="5"
                allow-half
              >
              </el-rate>
              <span class="service-rating-count"
                >({{ totalReviews }}条评价)</span
              >
              <span class="service-sales"
                >已售{{ serviceInfo.salesNumber }}单</span
              >
            </div>

            <div class="service-price-section">
              <span class="service-current-price"
                >¥{{ serviceInfo.servicePrice }}</span
              >
              <span class="service-price-unit"
                >/{{ getIntervalTimeText(serviceInfo.intervalTime) }}</span
              >
            </div>

            <div class="service-basic-info">
              <div class="basic-info-item">
                <span class="info-label">商户：</span>
                <span class="info-value">{{ serviceInfo.merchantName }}</span>
              </div>
              <div class="basic-info-item">
                <span class="info-label">地址：</span>
                <span class="info-value">{{ serviceInfo.address }}</span>
              </div>
            </div>
          </div>

          <div class="service-images-section">
            <h3 class="images-title">服务图片</h3>
            <div class="images-container">
              <div
                v-for="(image, index) in serviceImages"
                :key="index"
                class="image-item"
              >
                <img
                  :src="image"
                  :alt="`${serviceInfo.name} - 服务图片${index + 1}`"
                  @click="previewImage(image)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Tab导航 -->
        <div class="service-detail-tabs">
          <div class="tab-navigation">
            <button
              v-for="(tab, index) in tabList"
              :key="index"
              class="tab-nav-item"
              :class="{ active: currentTab === index }"
              @click="currentTab = index"
            >
              {{ tab.name }}
            </button>
          </div>

          <div class="tab-content">
            <!-- 预约下单 -->
            <div v-if="currentTab === 0" class="tab-panel">
              <div class="appointment-section">
                <h3>预约下单</h3>

                <div class="appointment-content">
                  <!-- 左侧表单区域 -->
                  <div class="appointment-left">
                    <!-- 高级预约组件 -->
                    <AdvancedAppointment
                      :service-info="serviceInfo"
                      :appointment-parameter.sync="appointmentParameter"
                      :weekly-times="weeklyTimes"
                      :have-made-an-appointment="haveMadeAnAppointment"
                    />

                    <div class="appointment-form">
                      <div class="form-group">
                        <label><i class="el-icon-user"></i>联系信息</label>
                        <div class="contact-form">
                          <el-input
                            v-model="contactInfo.name"
                            placeholder="联系人姓名"
                            prefix-icon="el-icon-user-solid"
                          ></el-input>
                          <el-input
                            v-model="contactInfo.phone"
                            placeholder="联系电话"
                            prefix-icon="el-icon-phone"
                          ></el-input>
                        </div>
                      </div>

                      <div
                        class="form-group"
                        v-if="serviceInfo.serviceWay === 2"
                      >
                        <label><i class="el-icon-location"></i>服务地址</label>
                        <el-input
                          v-model="serviceAddress"
                          type="textarea"
                          :rows="3"
                          placeholder="请输入详细服务地址（上门服务必填）"
                          maxlength="200"
                          show-word-limit
                        ></el-input>
                      </div>
                    </div>

                    <!-- 配套商品 -->
                    <div class="goods-section">
                      <h4>配套商品</h4>
                      <div v-if="goodsList.length > 0" class="goods-list">
                        <div
                          v-for="goods in goodsList"
                          :key="goods.id"
                          class="goods-item"
                        >
                          <img
                            :src="getImageUrl(goods.avatar)"
                            :alt="goods.name"
                            class="goods-image"
                          />
                          <div class="goods-info">
                            <h5 class="goods-name">{{ goods.name }}</h5>
                            <p class="goods-desc">{{ goods.goodsDesc }}</p>
                            <div class="goods-price">¥{{ goods.price }}</div>
                          </div>
                          <div class="goods-actions">
                            <div class="quantity-selector">
                              <button
                                @click="decreaseQuantity(goods)"
                                :disabled="goods.quantity <= 0"
                              >
                                -
                              </button>
                              <span>{{ goods.quantity || 0 }}</span>
                              <button @click="increaseQuantity(goods)">
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="no-goods">
                        <p>暂无配套商品</p>
                      </div>
                    </div>
                  </div>

                  <!-- 右侧价格和按钮区域 -->
                  <div class="appointment-right">
                    <div class="appointment-summary">
                      <div class="service-info">
                        <h4>服务信息</h4>
                        <div class="service-details">
                          <div class="detail-item">
                            <span class="label">服务名称：</span>
                            <span class="value">{{ serviceInfo.name }}</span>
                          </div>
                          <div class="detail-item">
                            <span class="label">服务方式：</span>
                            <span class="value">{{
                              getServiceWayText(serviceInfo.serviceWay)
                            }}</span>
                          </div>
                          <div class="detail-item">
                            <span class="label">服务时长：</span>
                            <span class="value">{{
                              getIntervalTimeText(serviceInfo.intervalTime)
                            }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="price-summary">
                        <div class="price-item">
                          <span class="label">服务费用：</span>
                          <span class="value"
                            >¥{{ serviceInfo.servicePrice }}</span
                          >
                        </div>
                        <div v-if="goodsTotal > 0" class="price-item">
                          <span class="label">商品费用：</span>
                          <span class="value">¥{{ goodsTotal }}</span>
                        </div>
                        <div class="total-price">
                          <span class="label">总计：</span>
                          <span class="amount">¥{{ totalAmount }}</span>
                        </div>
                      </div>

                      <div class="appointment-actions">
                        <button
                          class="appointment-btn"
                          @click="showQRCodeDialog"
                        >
                          <i class="el-icon-mobile-phone"></i>
                          立即预约
                        </button>
                        <button class="consult-btn" @click="openChat">
                          <i class="el-icon-chat-dot-round"></i>
                          {{
                            $store.getters["user/isLoggedIn"]
                              ? "咨询客服"
                              : "登录聊天"
                          }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 服务详情 -->
            <div v-if="currentTab === 1" class="tab-panel">
              <div class="service-description">
                <h3>服务详情</h3>
                <div
                  class="description-content"
                  v-html="serviceInfo.serviceDesc"
                ></div>

                <div v-if="serviceImages.length > 0" class="detail-images">
                  <h4>服务图片</h4>
                  <div class="detail-image-grid">
                    <img
                      v-for="(img, index) in serviceImages"
                      :key="index"
                      :src="img"
                      :alt="`服务图片${index + 1}`"
                      @click="previewImage(img)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- 服务人员 -->
            <div v-if="currentTab === 2" class="tab-panel">
              <div class="staff-section">
                <h3>服务人员</h3>
                <div v-if="staffList.length > 0" class="staff-main-content">
                  <!-- 员工选择列表 -->
                  <div class="staff-list">
                    <div
                      v-for="staff in staffList"
                      :key="staff.userId"
                      class="staff-item"
                      :class="{
                        selected: selectedStaff?.userId === staff.userId,
                      }"
                      @click="selectStaff(staff)"
                    >
                      <img
                        :src="getImageUrl(staff.avatar)"
                        :alt="staff.nickname"
                        class="staff-avatar"
                      />
                      <div class="staff-basic">
                        <div class="staff-name">{{ staff.nickname }}</div>
                        <div class="staff-tags">
                          <span class="staff-type-tag">{{
                            getStaffTypeText(staff.type)
                          }}</span>
                          <span class="staff-sex-tag">{{
                            getSexText(staff.sex)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 选中员工详情 -->
                  <div v-if="selectedStaff" class="staff-detail">
                    <div class="staff-detail-header">
                      <img
                        :src="getImageUrl(selectedStaff.avatar)"
                        :alt="selectedStaff.nickname"
                        class="staff-detail-avatar"
                      />
                      <div class="staff-detail-info">
                        <h3>{{ selectedStaff.nickname }}</h3>
                        <div class="staff-detail-tags">
                          <span class="tag">{{
                            getStaffTypeText(selectedStaff.type)
                          }}</span>
                          <span class="tag">{{
                            getSexText(selectedStaff.sex)
                          }}</span>
                        </div>
                        <button
                          class="chat-btn"
                          @click="chatWithStaff(selectedStaff)"
                        >
                          <i class="el-icon-chat-dot-round"></i>
                          {{
                            $store.getters["user/isLoggedIn"]
                              ? "聊天"
                              : "登录聊天"
                          }}
                        </button>
                      </div>
                    </div>

                    <div class="staff-description">
                      <h4>个人简介</h4>
                      <p>{{ selectedStaff.staffDesc }}</p>
                    </div>

                    <div v-if="staffImages.length > 0" class="staff-images">
                      <h4>相关图片</h4>
                      <div class="staff-image-grid">
                        <img
                          v-for="(img, index) in staffImages"
                          :key="index"
                          :src="img"
                          :alt="`${selectedStaff.nickname} - 图片${index + 1}`"
                          @click="previewImage(img)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="no-staff">
                  <p>暂无服务人员信息</p>
                </div>
              </div>
            </div>

            <!-- 店铺详情 -->
            <div v-if="currentTab === 3" class="tab-panel">
              <div class="merchant-section">
                <h3>店铺详情</h3>
                <div v-if="merchantInfo" class="merchant-info">
                  <div class="merchant-header">
                    <img
                      v-if="merchantInfo.merchantCover"
                      :src="getImageUrl(merchantInfo.merchantCover)"
                      :alt="merchantInfo.name"
                      class="merchant-cover"
                    />
                    <div class="merchant-basic">
                      <h4 class="merchant-name">{{ merchantInfo.name }}</h4>
                      <p class="merchant-area">{{ merchantInfo.area }}</p>
                      <p class="merchant-address">{{ merchantInfo.address }}</p>
                      <div class="merchant-contact">
                        <span class="contact-phone">
                          <i class="el-icon-phone"></i>
                          {{ formatPhonePrivacy(merchantInfo.phone) }}
                        </span>
                        <span class="business-hours">
                          营业时间：{{ merchantInfo.businessStartTime }} -
                          {{ merchantInfo.businessEndTime }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="merchant-description">
                    <h4>店铺介绍</h4>
                    <p>{{ merchantInfo.remark }}</p>
                  </div>

                  <div
                    v-if="merchantShopImages.length > 0"
                    class="merchant-images"
                  >
                    <h4>店铺图片</h4>
                    <div class="shop-image-grid">
                      <img
                        v-for="(img, index) in merchantShopImages"
                        :key="index"
                        :src="img"
                        :alt="`店铺图片${index + 1}`"
                        @click="previewImage(img)"
                      />
                    </div>
                  </div>

                  <div
                    v-if="merchantCertImages.length > 0"
                    class="merchant-certificates"
                  >
                    <h4>资质证书</h4>
                    <div class="cert-image-grid">
                      <img
                        v-for="(img, index) in merchantCertImages"
                        :key="index"
                        :src="img"
                        :alt="`资质证书${index + 1}`"
                        @click="previewImage(img)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 服务评价 -->
            <div v-if="currentTab === 4" class="tab-panel">
              <div class="reviews-section">
                <h3>服务评价</h3>
                <div class="reviews-summary">
                  <div class="summary-rating">
                    <span class="overall-rating">{{
                      serviceInfo.evaluate
                    }}</span>
                    <el-rate
                      v-model="ratingValue"
                      disabled
                      show-score
                      text-color="#ff9900"
                      :max="5"
                      allow-half
                    >
                    </el-rate>
                  </div>
                  <div class="summary-stats">
                    <span>共{{ totalReviews }}条评价</span>
                  </div>
                </div>

                <div v-if="reviewsList.length > 0" class="reviews-list">
                  <div
                    v-for="review in reviewsList"
                    :key="review.id"
                    class="review-item"
                  >
                    <div class="review-header">
                      <div class="review-user">
                        <div class="user-avatar">
                          {{ maskUserName(review.createName).charAt(0) }}
                        </div>
                        <div class="user-name">
                          {{ maskUserName(review.createName) }}
                        </div>
                        <div class="review-rating">
                          <el-rate v-model="review.rating" disabled :max="5">
                          </el-rate>
                        </div>
                      </div>
                      <div class="review-date">
                        {{ formatDate(review.createTime) }}
                      </div>
                    </div>
                    <div class="review-content">{{ review.content }}</div>
                    <div v-if="review.images" class="review-images">
                      <img
                        v-for="(img, index) in getReviewImages(review.images)"
                        :key="index"
                        :src="img"
                        :alt="`评价图片${index + 1}`"
                        @click="previewImage(img)"
                      />
                    </div>
                  </div>
                </div>
                <div v-else class="no-reviews">
                  <p>暂无评价</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 错误状态 -->
      <div v-else class="error-state">
        <i class="el-icon-warning"></i>
        <p>服务信息不存在</p>
        <button class="error-btn" @click="goBack">返回列表</button>
      </div>
    </div>

    <!-- 二维码预约弹窗 -->
    <el-dialog
      title="扫码预约服务"
      :visible.sync="qrCodeDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      custom-class="qr-code-dialog"
      center
    >
      <div class="qr-code-content">
        <div class="qr-code-header">
          <p>请使用微信扫描下方二维码进入"三火私服"小程序完成预约</p>
        </div>

        <div class="qr-code-main">
          <div class="qr-code-container">
            <img :src="qrCodeUrl" alt="小程序二维码" class="qr-code-image" />
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 导入字典配置
import {
  ORDER_TYPE_OPTIONS,
  INTERVAL_TIME_OPTIONS,
  SERVICE_WAY_OPTIONS,
  SEX_OPTIONS,
  STAFF_TYPE_OPTIONS,
  getDictionaryLabel,
} from "@/config/dictionaries";
// 导入图片处理工具
import { getImageUrl } from "@/utils/imageUtils";
// 导入API
import { noPermissionRequired } from "@/api/index";
import API from "@/api"; // 添加消息API
import { formatPhonePrivacy } from "@/utils/common";
// 导入高级预约组件
import AdvancedAppointment from "@/components/AdvancedAppointment.vue";
// 导入工具函数
import { calculateTotalPrice } from "@/utils/timeSlotsUtil";

export default {
  name: "ServiceDetail",
  components: {
    AdvancedAppointment,
  },
  data() {
    return {
      loading: true,
      currentTab: 0,
      serviceInfo: null,
      merchantInfo: null,
      goodsList: [],
      staffList: [],
      selectedStaff: null,
      reviewsList: [],
      totalSales: 0,
      appointmentParameter: {
        date: "",
        time: "",
        dateList: [],
      },
      contactInfo: {
        name: "",
        phone: "",
      },
      serviceAddress: "",
      weeklyTimes: [],
      haveMadeAnAppointment: [],
      qrCodeDialogVisible: false,
      tabList: [
        { name: "预约下单" },
        { name: "服务详情" },
        { name: "服务人员" },
        { name: "店铺详情" },
        { name: "服务评价" },
      ],
    };
  },

  computed: {
    serviceImages() {
      if (!this.serviceInfo || !this.serviceInfo.imgList)
        return [require("@/assets/logo.png")];
      const images = this.serviceInfo.imgList
        .split(",")
        .filter((img) => img.trim());
      return images.length > 0
        ? images.map((img) => getImageUrl(img.trim()))
        : [require("@/assets/logo.png")];
    },

    merchantShopImages() {
      if (!this.merchantInfo || !this.merchantInfo.shopPictureImgList)
        return [];
      return this.merchantInfo.shopPictureImgList
        .split(",")
        .filter((img) => img.trim())
        .map((img) => getImageUrl(img.trim()));
    },

    merchantCertImages() {
      if (!this.merchantInfo || !this.merchantInfo.certificationImgList)
        return [];
      return this.merchantInfo.certificationImgList
        .split(",")
        .filter((img) => img.trim())
        .map((img) => getImageUrl(img.trim()));
    },

    ratingValue: {
      get() {
        return this.serviceInfo
          ? parseFloat(this.serviceInfo.evaluate) || 0
          : 0;
      },
      set() {
        // 只读属性，不允许修改
      },
    },

    totalReviews() {
      return this.reviewsList.length;
    },

    goodsTotal() {
      let total = 0;
      this.goodsList.forEach((goods) => {
        total += parseFloat(goods.price || 0) * (goods.quantity || 0);
      });
      return total.toFixed(2);
    },

    totalAmount() {
      if (!this.serviceInfo) return "0.00";

      const serviceCount =
        this.serviceInfo.orderType === "1"
          ? this.appointmentParameter.dateList.length
          : 1;

      return calculateTotalPrice(
        this.goodsList,
        this.serviceInfo.servicePrice,
        serviceCount
      );
    },

    datePickerOptions() {
      return {
        disabledDate: (time) => {
          return time.getTime() < Date.now() - 24 * 60 * 60 * 1000;
        },
      };
    },

    qrCodeUrl() {
      return require("@/assets/miniprogram-qr.png");
    },

    // 员工图片列表
    staffImages() {
      if (!this.selectedStaff || !this.selectedStaff.imgList) return [];
      return this.selectedStaff.imgList
        .split(",")
        .filter((img) => img.trim())
        .map((img) => getImageUrl(img.trim()));
    },
  },

  created() {
    this.loadServiceDetail();
  },

  methods: {
    /**
     * 手机号隐私处理
     */
    formatPhonePrivacy,

    /**
     * 加载服务详情 - 使用真实API数据
     * 包括服务基础信息、商户信息、关联商品和人员信息
     */
    async loadServiceDetail() {
      this.loading = true;
      try {
        const serviceId = this.$route.params.id;
        if (!serviceId) {
          throw new Error("缺少服务ID参数");
        }

        // 加载服务基础信息
        const serviceResponse =
          await noPermissionRequired.getServiceDetailNoToken(serviceId);
        if (!serviceResponse || !serviceResponse.data) {
          throw new Error("服务不存在或已下架");
        }
        this.serviceInfo = serviceResponse.data;

        // 从API响应中提取预约配置数据
        this.weeklyTimes = serviceResponse.data.weeklyTimes || [];
        this.haveMadeAnAppointment = this.formatHistoryOrderTimes(
          serviceResponse.data.historyOrderTimes || []
        );

        // 加载商户信息
        if (this.serviceInfo.merchantId) {
          try {
            const merchantResponse = await noPermissionRequired.getMerchantID(
              this.serviceInfo.merchantId
            );
            this.merchantInfo = merchantResponse?.data || null;
          } catch (error) {
            console.error("加载商户信息失败:", error);
            this.merchantInfo = null;
          }
        }

        // 加载服务关联信息（商品和人员）
        try {
          const relationsResponse = await noPermissionRequired.serviceRelations(
            serviceId
          );
          if (relationsResponse && relationsResponse.data) {
            // 从关联信息中提取员工列表
            this.staffList = relationsResponse.data.staffList || [];
            // 初始化员工状态并默认选择第一个员工
            this.staffList = this.staffList.map((staff) => ({
              ...staff,
              isOptFor: false,
            }));

            // 默认选择第一个员工
            if (this.staffList.length > 0) {
              this.selectedStaff = { ...this.staffList[0], isOptFor: true };
              this.staffList[0].isOptFor = true;
            }

            // 从关联信息中提取商品列表
            this.goodsList = relationsResponse.data.goodsList || [];
            // 初始化商品数量
            this.goodsList.forEach((goods) => {
              this.$set(goods, "quantity", 0);
            });
          } else {
            this.staffList = [];
            this.goodsList = [];
          }
        } catch (error) {
          console.error("加载服务关联信息失败:", error);
          this.staffList = [];
          this.goodsList = [];
        }

        // 加载评价信息
        try {
          const reviewParams = {
            current: 1, // 页码，从1开始
            size: 99, // 每页数量，获取99条数据
            status: 1, // 评价状态：1-已发布/有效的评价
            serviceId: serviceId, // 服务ID，用于获取特定服务的评价
          };
          const reviewResponse = await noPermissionRequired.getReview(
            reviewParams
          );
          if (
            reviewResponse &&
            reviewResponse.data &&
            reviewResponse.data.records
          ) {
            this.reviewsList = reviewResponse.data.records;
            // 为每个评价设置评分值（用于显示星级）
            this.reviewsList.forEach((review) => {
              // 优先使用 rating 字段，如果没有则使用 evaluate 字段
              const ratingValue = review.rating || review.evaluate;
              const rating = parseFloat(ratingValue);
              this.$set(review, "rating", isNaN(rating) ? 0 : rating);
            });
          } else {
            this.reviewsList = [];
          }
        } catch (error) {
          console.error("加载评价信息失败:", error);
          this.reviewsList = [];
        }

        this.totalSales = parseInt(this.serviceInfo.salesNumber) || 0;
      } catch (error) {
        console.error("加载服务详情失败:", error);
        this.$message.error(error.message || "加载服务详情失败，请稍后重试");
        this.serviceInfo = null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 格式化历史预约时间数据
     * 将API返回的historyOrderTimes转换为组件所需的格式
     */
    formatHistoryOrderTimes(historyOrderTimes) {
      if (!Array.isArray(historyOrderTimes) || historyOrderTimes.length === 0) {
        return [];
      }

      return historyOrderTimes
        .map((item) => {
          // 假设API返回的数据格式包含日期和时间信息
          // 根据实际API数据结构调整这里的逻辑
          if (item.appointmentDate && item.appointmentTime) {
            const date = new Date(item.appointmentDate);
            const dateStr = `${date.getMonth() + 1}-${date.getDate()}`;
            return {
              type: "time",
              date: dateStr,
              time: item.appointmentTime,
            };
          }
          return null;
        })
        .filter(Boolean); // 过滤掉null值
    },

    /**
     * 返回列表
     */
    goBack() {
      this.$router.go(-1);
    },

    /**
     * 获取图片URL
     */
    getImageUrl(imageName) {
      return getImageUrl(imageName);
    },

    /**
     * 预览图片
     */
    previewImage(image) {
      // TODO: 实现图片预览功能
    },

    /**
     * 增加商品数量
     */
    increaseQuantity(goods) {
      this.$set(goods, "quantity", (goods.quantity || 0) + 1);
    },

    /**
     * 减少商品数量
     */
    decreaseQuantity(goods) {
      if (goods.quantity > 0) {
        this.$set(goods, "quantity", goods.quantity - 1);
      }
    },

    /**
     * 选择员工
     */
    selectStaff(staff) {
      // 重置所有员工选择状态
      this.staffList.forEach((s) => {
        s.isOptFor = false;
      });

      // 设置选中员工
      staff.isOptFor = true;
      this.selectedStaff = { ...staff };
    },

    /**
     * 与员工聊天
     */
    async chatWithStaff(staff) {
      // 检查登录状态
      if (!this.$store.getters["user/isLoggedIn"]) {
        this.$msg.warning("请先登录后再进行聊天");
        this.$bus.emit("show-login-dialog");
        return;
      }

      // 显示加载提示
      const closeLoading = this.$msg.loading("正在加载聊天记录...");

      try {
        // 获取聊天历史记录 - 使用正确的字段名 userId
        const chatHistory = await this.loadChatHistory(staff.userId);

        // 关闭加载提示
        closeLoading();

        // 使用选中的员工ID打开聊天对话框 - 使用正确的字段名
        this.$bus.emit("show-chat-dialog", {
          employeeId: staff.userId,
          employeeName: staff.nickname,
          chatType: "employee",
          chatHistory: chatHistory,
        });
      } catch (error) {
        // 关闭加载提示
        closeLoading();

        console.error("获取聊天历史失败:", error);

        // 即使失败也要打开聊天框
        this.$bus.emit("show-chat-dialog", {
          employeeId: staff.userId,
          employeeName: staff.nickname,
          chatType: "employee",
          chatHistory: [],
        });

        this.$msg.warning("获取聊天历史记录失败，但聊天功能正常");
      }
    },

    /**
     * 打开客服聊天
     */
    async openChat() {
      // 检查登录状态
      if (!this.$store.getters["user/isLoggedIn"]) {
        this.$msg.warning("请先登录后再进行聊天");
        this.$bus.emit("show-login-dialog");
        return;
      }

      // 使用第一个员工的ID作为客服聊天
      if (this.staffList && this.staffList.length > 0) {
        const firstEmployee = this.staffList[0];

        // 显示加载提示
        const closeLoading = this.$msg.loading("正在加载聊天记录...");

        try {
          // 获取聊天历史记录 - 使用正确的字段名 userId
          const chatHistory = await this.loadChatHistory(firstEmployee.userId);

          // 关闭加载提示
          closeLoading();

          this.$bus.emit("show-chat-dialog", {
            employeeId: firstEmployee.userId,
            employeeName: firstEmployee.nickname,
            chatType: "service",
            chatHistory: chatHistory,
          });
        } catch (error) {
          // 关闭加载提示
          closeLoading();

          console.error("获取聊天历史失败:", error);

          // 即使失败也要打开聊天框
          this.$bus.emit("show-chat-dialog", {
            employeeId: firstEmployee.userId,
            employeeName: firstEmployee.nickname,
            chatType: "service",
            chatHistory: [],
          });

          this.$msg.warning("获取聊天历史记录失败，但聊天功能正常");
        }
      } else {
        this.$msg.warning("暂无可用的客服人员");
      }
    },

    /**
     * 获取聊天历史记录
     * @param {string} toUserId - 聊天对象用户ID
     * @returns {Promise<Array>} 聊天历史记录数组
     */
    async loadChatHistory(toUserId) {
      try {
        // 获取当前用户ID
        const userInfo = this.$store.getters["user/userInfo"];
        const currentUserId = userInfo?.id || userInfo?.userId;

        if (!currentUserId) {
          console.warn("无法获取当前用户ID，跳过获取聊天历史");
          return [];
        }

        console.log("正在获取聊天历史记录:", {
          fromUserId: currentUserId,
          toUserId,
        });

        // 调用聊天历史记录接口
        const response = await API.message.messageChatHistory(
          currentUserId,
          toUserId
        );

        if (response && response.code === 0 && response.data) {
          console.log(
            "聊天历史记录获取成功，共",
            response.data.length,
            "条记录"
          );
          return this.formatChatHistory(response.data);
        } else {
          console.warn("获取聊天历史失败:", response?.msg || "未知错误");
          return [];
        }
      } catch (error) {
        console.error("获取聊天历史记录失败:", error);
        return [];
      }
    },

    /**
     * 格式化聊天历史记录数据
     * @param {Array} historyList - 原始历史记录数据
     * @returns {Array} 格式化后的聊天记录
     */
    formatChatHistory(historyList) {
      if (!Array.isArray(historyList)) {
        return [];
      }

      return historyList.map((message) => ({
        id: message.id,
        fromUserId: message.fromUserId,
        fromUserName: message.fromUserName,
        toUserId: message.toUserId,
        toUserName: message.toUserName,
        content: message.content,
        msgType: message.msgType || "3",
        orderId: message.orderId,
        page: message.page,
        readStatus: message.readStatus,
        createTime: message.createTime,
        updateTime: message.updateTime,
        isRevoked: message.isRevoked || false,
        extraData: message.extraData || {},
        // 添加实际API返回的字段
        createId: message.createId,
        updateId: message.updateId,
        delFlag: message.delFlag,
        tenantId: message.tenantId,
        timeSort: message.timeSort,
      }));
    },

    /**
     * 格式化日期
     */
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("zh-CN");
    },

    /**
     * 脱敏用户名
     */
    maskUserName(name) {
      if (!name || name.length <= 2) return name;
      return (
        name.charAt(0) +
        "*".repeat(name.length - 2) +
        name.charAt(name.length - 1)
      );
    },

    /**
     * 获取评价图片列表
     */
    getReviewImages(imagesStr) {
      if (!imagesStr) return [];
      return imagesStr
        .split(",")
        .filter((img) => img.trim())
        .map((img) => getImageUrl(img.trim()));
    },

    /**
     * 获取接单类型文本
     */
    getOrderTypeText(value) {
      return getDictionaryLabel(ORDER_TYPE_OPTIONS, value);
    },

    /**
     * 获取服务时长文本
     */
    getIntervalTimeText(value) {
      return getDictionaryLabel(INTERVAL_TIME_OPTIONS, value);
    },

    /**
     * 获取服务方式文本
     */
    getServiceWayText(value) {
      return getDictionaryLabel(SERVICE_WAY_OPTIONS, parseInt(value));
    },

    /**
     * 获取性别文本
     */
    getSexText(value) {
      return getDictionaryLabel(SEX_OPTIONS, value);
    },

    /**
     * 获取员工类型文本
     */
    getStaffTypeText(value) {
      return getDictionaryLabel(STAFF_TYPE_OPTIONS, value);
    },

    /**
     * 刷新预约数据 - 重新从API获取最新的预约时间
     * 当用户提交预约后可调用此方法刷新数据
     */
    async refreshAppointmentData() {
      if (!this.serviceInfo?.id) return;

      try {
        const serviceResponse =
          await noPermissionRequired.getServiceDetailNoToken(
            this.serviceInfo.id
          );
        if (serviceResponse?.data) {
          // 只更新预约相关数据
          this.weeklyTimes = serviceResponse.data.weeklyTimes || [];
          this.haveMadeAnAppointment = this.formatHistoryOrderTimes(
            serviceResponse.data.historyOrderTimes || []
          );
        }
      } catch (error) {
        console.error("刷新预约数据失败:", error);
      }
    },

    /**
     * 显示二维码弹窗
     */
    showQRCodeDialog() {
      this.qrCodeDialogVisible = true;
    },
  },
};
</script>

<style lang="scss" scoped>
// 🛡️ 使用局部作用域的 CSS 变量，避免全局污染
.service-detail-page {
  --service-detail-primary: #e74c3c;
  --service-detail-primary-dark: #c0392b;
  --service-detail-primary-light: #f8d7da;
  --service-detail-border-radius: 12px;
  --service-detail-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  --service-detail-transition: all 0.3s ease;
}

// 红色主题色彩定义
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;

// 🎯 使用命名空间前缀避免 mixin 名称冲突
// 卡片样式混合
@mixin service-detail-glass-card {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(248, 249, 250, 0.9) 100%
  );
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--service-detail-border-radius);
  box-shadow: var(--service-detail-shadow);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(231, 76, 60, 0.02) 0%,
      rgba(255, 255, 255, 0.05) 100%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

// 按钮样式混合
@mixin service-detail-primary-button {
  background: linear-gradient(
    135deg,
    var(--service-detail-primary) 0%,
    var(--service-detail-primary-dark) 100%
  );
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: var(--service-detail-transition);
  font-weight: 600;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// 次要按钮样式混合
@mixin service-detail-secondary-button {
  background: rgba(255, 255, 255, 0.9);
  color: var(--service-detail-primary);
  border: 1px solid var(--service-detail-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: var(--service-detail-transition);
  font-weight: 600;

  &:hover {
    background: var(--service-detail-primary);
    color: white;
    transform: translateY(-1px);
  }
}

// 图片悬停效果混合
// 网格布局混合
@mixin service-detail-responsive-grid($min-width: 100px) {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax($min-width, 1fr));
  gap: 16px;
}

// 截断文本混合
@mixin service-detail-text-ellipsis($lines: 1) {
  @if $lines == 1 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  } @else {
    display: -webkit-box;
    -webkit-line-clamp: $lines;
    line-clamp: $lines;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

// 加载动画混合
@mixin service-detail-loading-skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: service-detail-loading 1.5s infinite;

  @keyframes service-detail-loading {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
}

// 状态标签混合
@mixin service-detail-status-badge(
  $bg-color,
  $text-color,
  $border-color: $bg-color
) {
  padding: 6px 12px;
  background: $bg-color;
  color: $text-color;
  border: 1px solid $border-color;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

// 🔒 受限作用域的表单组件样式保护混合
// 只影响当前组件内的元素，不会污染全局
@mixin service-detail-form-protection {
  // 已经在 scoped 环境中，不需要额外嵌套
  ::v-deep(.el-input),
  ::v-deep(.el-textarea),
  ::v-deep(.el-select),
  ::v-deep(.el-date-editor),
  ::v-deep(.el-time-picker) {
    .el-input__inner,
    .el-textarea__inner {
      border-radius: 4px !important;
      border-color: #dcdfe6 !important;
      font-family: inherit !important;

      &:focus {
        border-color: var(--service-detail-primary) !important;
      }
    }
  }

  ::v-deep(.el-rate) {
    height: auto !important;
    line-height: normal !important;

    .el-rate__item {
      font-size: 16px !important;
    }
  }
}

.service-detail-page {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 12px 0;

  // 应用表单保护样式
  @include service-detail-form-protection;

  // 防止外部样式污染
  * {
    box-sizing: border-box;
  }

  // 重置可能被污染的样式
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-weight: normal;
  }

  p {
    margin: 0;
  }

  ul,
  ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
}

.service-detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

/* 加载状态 */
.service-detail-loading-wrapper {
  .service-detail-skeleton-container {
    background: white;
    border-radius: 6px;
    padding: 16px;
    margin-bottom: 16px;

    .service-detail-skeleton-line {
      height: 14px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
      border-radius: 3px;
      margin-bottom: 8px;

      &:nth-child(1) {
        width: 60%;
      }
      &:nth-child(2) {
        width: 100%;
      }
      &:nth-child(3) {
        width: 80%;
      }
      &:nth-child(4) {
        width: 90%;
      }
      &:nth-child(5) {
        width: 70%;
      }
      &:nth-child(6) {
        width: 85%;
      }
      &:nth-child(7) {
        width: 95%;
      }
      &:nth-child(8) {
        width: 75%;
      }
    }
  }
}

/* 返回按钮 */
.service-detail-back-button {
  margin-bottom: 12px;

  .service-detail-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: white;
    border: 1px solid #dcdfe6;
    border-radius: 3px;
    color: #333;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 13px;

    &:hover {
      border-color: $primary-red;
      color: $primary-red;
    }
  }
}

/* 主要内容区域 */
.service-detail-content {
  > div {
    @include service-detail-glass-card;
    margin-bottom: 16px;
    padding: 20px;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        rgba(231, 76, 60, 0.02) 0%,
        rgba(255, 255, 255, 0.05) 100%
      );
      pointer-events: none;
      z-index: 0;
    }

    > * {
      position: relative;
      z-index: 1;
    }
  }
}

/* 服务主卡片 */
.service-main-card {
  display: flex;
  gap: 32px;
  padding: 20px;
  min-height: 400px;

  .service-info-section {
    flex: 1; // 占据一半空间
    min-width: 0; // 防止flex子项溢出
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .service-title {
      font-size: 28px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 16px;
      line-height: 1.2;
      flex-shrink: 0;
    }

    .service-status-tags {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
      flex-shrink: 0;

      .service-status-tag {
        padding: 6px 10px;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 600;

        &.online {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        &.offline {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
      }

      .service-way-tag,
      .order-type-tag {
        padding: 6px 10px;
        background: #fff3cd;
        color: #856404;
        border: 1px solid #ffeaa7;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 600;
      }
    }

    .service-rating-section {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      gap: 12px;
      flex-shrink: 0;

      .service-rating-count {
        color: #7f8c8d;
        font-size: 15px;
      }

      .service-sales {
        color: #7f8c8d;
        font-size: 15px;
      }
    }

    .service-price-section {
      margin-bottom: 16px;
      padding: 16px 20px;
      background: linear-gradient(
        135deg,
        rgba(231, 76, 60, 0.1) 0%,
        rgba(255, 255, 255, 0.8) 100%
      );
      backdrop-filter: blur(10px);
      border: 1px solid rgba(231, 76, 60, 0.2);
      border-radius: 12px;
      position: relative;
      overflow: hidden;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      min-height: 60px;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(
          circle at top right,
          rgba(231, 76, 60, 0.15) 0%,
          transparent 70%
        );
        pointer-events: none;
      }

      .service-current-price {
        font-size: 32px;
        font-weight: 800;
        background: linear-gradient(45deg, $primary-red, $primary-red-dark);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1;
        position: relative;
        z-index: 1;
      }

      .service-price-unit {
        font-size: 16px;
        color: #666;
        margin-left: 6px;
        font-weight: 500;
        position: relative;
        z-index: 1;
      }
    }

    .service-basic-info {
      flex-shrink: 0;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(231, 76, 60, 0.1);
      border-radius: 10px;
      padding: 14px 16px;
      min-height: 70px;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .basic-info-item {
        display: flex;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        .info-label {
          font-size: 15px;
          color: #666;
          width: 70px;
          flex-shrink: 0;
          font-weight: 600;
        }

        .info-value {
          font-size: 15px;
          color: #333;
          flex: 1;
          font-weight: 500;
          line-height: 1.4;
        }
      }
    }
  }

  .service-images-section {
    flex: 1; // 占据一半空间
    min-width: 0; // 防止flex子项溢出

    .images-title {
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 2px solid $primary-red;
    }

    .images-container {
      height: 350px;
      overflow-y: auto;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      padding: 12px;
      background: #fafafa;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb {
        background: $primary-red;
        border-radius: 3px;

        &:hover {
          background: $primary-red-dark;
        }
      }

      .image-item {
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        img {
          width: 100%;
          height: auto;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 1px solid #e0e0e0;

          &:hover {
            border-color: $primary-red;
            box-shadow: 0 4px 12px rgba(231, 76, 60, 0.2);
          }
        }
      }
    }
  }
}

/* Tab导航 */
.service-detail-tabs {
  .tab-navigation {
    display: flex;
    justify-content: space-between;
    background: linear-gradient(135deg, #fff 80%, #f8f9fa 100%);
    border-radius: 16px 16px 0 0;
    box-shadow: 0 4px 18px rgba(231, 76, 60, 0.06), 0 1.5px 0 #f5eaea;
    border: 1.5px solid #f5eaea;
    padding: 0 8px;
    position: relative;
    min-height: 56px;
    overflow: visible;

    .tab-nav-item {
      flex: 1;
      margin: 8px 8px 0 8px;
      padding: 12px 0 13px 0;
      border: none;
      background: none;
      color: #606266;
      cursor: pointer;
      transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
      font-size: 17px;
      font-weight: 500;
      border-radius: 12px 12px 0 0;
      text-align: center;
      position: relative;
      z-index: 1;
      box-shadow: 0 1px 2px rgba(231, 76, 60, 0.03);

      &:hover {
        color: $primary-red;
        background: rgba(231, 76, 60, 0.07);
      }

      &.active {
        color: $primary-red;
        font-weight: 700;
        background: #fff;
        box-shadow: 0 4px 16px rgba(231, 76, 60, 0.1), 0 2px 0 #fff;
        border-bottom: 2.5px solid $primary-red;
        z-index: 2;
      }
    }
  }

  .tab-content {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(248, 249, 250, 0.9) 100%
    );
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0 0 12px 12px;
    min-height: 400px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

    .tab-panel {
      padding: 24px;

      h3 {
        font-size: 22px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 20px;
        padding-bottom: 8px;
        border-bottom: 2px solid rgba(231, 76, 60, 0.2);
        position: relative;

        &::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(90deg, $primary-red, $primary-red-dark);
          border-radius: 1px;
        }
      }

      h4 {
        font-size: 18px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 14px;
        margin-top: 20px;
        display: flex;
        align-items: center;
        gap: 8px;

        &::before {
          content: "";
          width: 4px;
          height: 18px;
          background: linear-gradient(180deg, $primary-red, $primary-red-dark);
          border-radius: 2px;
        }
      }
    }
  }
}

/* 预约下单 */
.appointment-section {
  h3 {
    font-size: 22px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 20px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(231, 76, 60, 0.2);
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 60px;
      height: 2px;
      background: linear-gradient(90deg, $primary-red, $primary-red-dark);
      border-radius: 1px;
    }
  }

  .appointment-content {
    display: grid;
    grid-template-columns: 1fr 400px;
    gap: 32px;
    align-items: start;
  }

  .appointment-left {
    .appointment-form {
      margin-bottom: 20px;

      .form-group {
        margin-bottom: 14px;

        label {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-bottom: 6px;
          font-weight: 600;
          color: #2c3e50;
          font-size: 13px;

          i {
            color: $primary-red;
            font-size: 14px;
          }
        }

        .time-selector {
          display: flex;
          gap: 10px;

          .el-date-editor,
          .el-time-picker {
            flex: 1;
            min-width: 0;
          }
        }

        .contact-form {
          display: flex;
          gap: 10px;

          .el-input {
            flex: 1;
            min-width: 0;
          }
        }
      }
    }

    .goods-section {
      h4 {
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        gap: 6px;

        &::before {
          content: "";
          width: 3px;
          height: 16px;
          background: linear-gradient(180deg, $primary-red, $primary-red-dark);
          border-radius: 2px;
        }
      }

      .goods-list {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .goods-item {
          display: flex;
          align-items: center;
          padding: 12px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(231, 76, 60, 0.1);
          border-radius: 8px;
          gap: 12px;

          .goods-image {
            width: 60px;
            height: 60px;
            object-fit: cover;
            border-radius: 6px;
            flex-shrink: 0;
          }

          .goods-info {
            flex: 1;

            .goods-name {
              font-size: 14px;
              font-weight: 600;
              color: #2c3e50;
              margin: 0 0 4px 0;
            }

            .goods-desc {
              font-size: 12px;
              color: #666;
              margin: 0 0 6px 0;
              line-height: 1.3;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }

            .goods-price {
              font-size: 14px;
              font-weight: 600;
              color: $primary-red;
            }
          }

          .goods-actions {
            .quantity-selector {
              display: flex;
              align-items: center;
              background: rgba(255, 255, 255, 0.9);
              border: 1px solid rgba(231, 76, 60, 0.2);
              border-radius: 6px;
              overflow: hidden;

              button {
                width: 28px;
                height: 28px;
                border: none;
                background: rgba(255, 255, 255, 0.9);
                cursor: pointer;
                font-size: 14px;
                font-weight: 600;
                color: $primary-red;

                &:hover:not(:disabled) {
                  background: $primary-red;
                  color: white;
                }

                &:disabled {
                  opacity: 0.4;
                  cursor: not-allowed;
                }
              }

              span {
                width: 36px;
                text-align: center;
                border-left: 1px solid rgba(231, 76, 60, 0.2);
                border-right: 1px solid rgba(231, 76, 60, 0.2);
                line-height: 28px;
                background: rgba(255, 255, 255, 0.95);
                font-weight: 600;
                color: #2c3e50;
                font-size: 12px;
              }
            }
          }
        }
      }

      .no-goods {
        text-align: center;
        color: #999;
        padding: 20px;
        background: rgba(255, 255, 255, 0.6);
        border-radius: 8px;
        border: 1px solid rgba(231, 76, 60, 0.1);
        font-size: 14px;
      }
    }
  }

  .appointment-right {
    .appointment-summary {
      position: sticky;
      top: 20px;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95) 0%,
        rgba(248, 249, 250, 0.9) 100%
      );
      backdrop-filter: blur(15px);
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 12px;
      padding: 20px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

      .service-info {
        margin-bottom: 20px;
        padding-bottom: 16px;
        border-bottom: 1px solid rgba(231, 76, 60, 0.1);

        h4 {
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 12px;
        }

        .service-details {
          .detail-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 13px;

            .label {
              color: #666;
            }

            .value {
              color: #333;
              font-weight: 500;
            }
          }
        }
      }

      .price-summary {
        margin-bottom: 20px;

        .price-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;

          .label {
            color: #666;
          }

          .value {
            color: #333;
            font-weight: 500;
          }
        }

        .total-price {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-top: 1px solid rgba(231, 76, 60, 0.2);
          margin-top: 12px;

          .label {
            font-size: 16px;
            font-weight: 600;
            color: #2c3e50;
          }

          .amount {
            font-size: 20px;
            font-weight: 700;
            color: $primary-red;
          }
        }
      }

      .appointment-actions {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .appointment-btn {
          width: 100%;
          padding: 12px;
          @include service-detail-primary-button;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;

          &.submitting {
            opacity: 0.8;

            .el-icon-loading {
              animation: rotate 1s linear infinite;
            }
          }
        }

        .consult-btn {
          width: 100%;
          padding: 10px;
          @include service-detail-secondary-button;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
      }
    }
  }
}

/* 服务描述 */
.service-description {
  .description-content {
    line-height: 1.7;
    color: #555;
    font-size: 16px;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    padding: 20px;
    border-radius: 12px;
    border: 1px solid rgba(231, 76, 60, 0.1);
    margin-bottom: 24px;

    ::v-deep(p) {
      margin-bottom: 16px;
      text-indent: 2em;
    }

    ::v-deep(ul) {
      padding-left: 24px;
      margin-bottom: 16px;

      li {
        margin-bottom: 10px;
        position: relative;

        &::before {
          content: "•";
          color: $primary-red;
          font-weight: bold;
          position: absolute;
          left: -16px;
        }
      }
    }
  }

  .detail-images {
    .detail-image-grid {
      display: flex;
      flex-direction: column;
      gap: 12px;

      img {
        width: 100%;
        height: auto;
        border-radius: 12px;
      }
    }
  }
}

/* 服务人员 */
.staff-section {
  .staff-main-content {
    display: flex;
    gap: 32px;

    .staff-list {
      width: 320px;
      border-right: 1px solid #e8e8e8;
      padding-right: 32px;
      flex-shrink: 0;

      .staff-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        border: 1px solid #e8e8e8;
        border-radius: 12px;
        margin-bottom: 16px;
        cursor: pointer;
        background: white;
        transition: all 0.3s ease;

        &:hover {
          border-color: $primary-red;
          box-shadow: 0 2px 8px rgba(231, 76, 60, 0.1);
        }

        &.selected {
          border-color: $primary-red;
          background: rgba(231, 76, 60, 0.04);
          box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
        }

        .staff-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          border: 2px solid #f0f0f0;
        }

        .staff-basic {
          flex: 1;

          .staff-name {
            font-size: 15px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 8px;
          }

          .staff-tags {
            display: flex;
            gap: 8px;

            .staff-type-tag,
            .staff-sex-tag {
              padding: 3px 8px;
              background: #f5f5f5;
              color: #666;
              border-radius: 10px;
              font-size: 12px;
              font-weight: 500;
            }
          }
        }
      }
    }

    .staff-detail {
      flex: 1;

      .staff-detail-header {
        display: flex;
        gap: 20px;
        margin-bottom: 24px;
        align-items: flex-start;
        padding: 20px;
        background: #f9f9f9;
        border-radius: 12px;

        .staff-detail-avatar {
          width: 90px;
          height: 90px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .staff-detail-info {
          flex: 1;

          h3 {
            font-size: 20px;
            font-weight: 700;
            color: #2c3e50;
            margin-bottom: 12px;
          }

          .staff-detail-tags {
            display: flex;
            gap: 10px;
            margin-bottom: 16px;

            .tag {
              padding: 6px 12px;
              background: white;
              color: #666;
              border: 1px solid #e0e0e0;
              border-radius: 16px;
              font-size: 13px;
              font-weight: 500;
            }
          }

          .chat-btn {
            padding: 10px 20px;
            background: $primary-red;
            color: white;
            border-radius: 8px;
            font-size: 15px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: all 0.3s ease;

            &:hover {
              background: $primary-red-dark;
            }
          }
        }
      }

      .staff-description {
        margin-bottom: 24px;
        padding: 20px;
        background: #fafafa;
        border-radius: 10px;

        h4 {
          font-size: 17px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 12px;
        }

        p {
          font-size: 15px;
          color: #555;
          line-height: 1.7;
        }
      }

      .staff-images {
        h4 {
          font-size: 17px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 16px;
        }

        .staff-image-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;

          img {
            width: 100%;
            height: auto;
            object-fit: cover;
            border-radius: 10px;
            cursor: pointer;
            border: 1px solid #e8e8e8;
            display: block;
            transition: all 0.3s ease;

            &:hover {
              border-color: $primary-red;
              box-shadow: 0 4px 12px rgba(231, 76, 60, 0.2);
            }
          }
        }
      }
    }
  }

  .no-staff {
    text-align: center;
    color: #999;
    padding: 60px 20px;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 1px solid rgba(231, 76, 60, 0.1);
    font-size: 16px;
  }
}

/* 商户信息 */
.merchant-section {
  .merchant-info {
    .merchant-header {
      display: grid;
      grid-template-columns: 100px 1fr;
      gap: 20px;
      margin-bottom: 24px;
      padding: 20px;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9) 0%,
        rgba(248, 249, 250, 0.8) 100%
      );
      backdrop-filter: blur(15px);
      border: 1px solid rgba(231, 76, 60, 0.15);
      border-radius: 12px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

      .merchant-cover {
        width: 100px;
        height: 100px;
        object-fit: cover;
        border-radius: 12px;
        border: 3px solid rgba(255, 255, 255, 0.9);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      }

      .merchant-basic {
        .merchant-name {
          font-size: 20px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 10px;
          line-height: 1.3;
        }

        .merchant-area,
        .merchant-address {
          font-size: 15px;
          color: #666;
          margin-bottom: 6px;
          line-height: 1.4;
        }

        .merchant-contact {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-top: 12px;

          .contact-phone,
          .business-hours {
            font-size: 14px;
            color: #666;
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            background: rgba(255, 255, 255, 0.7);
            border-radius: 8px;
            border: 1px solid rgba(231, 76, 60, 0.1);
          }
        }
      }
    }

    .merchant-description {
      margin-bottom: 24px;
      padding: 18px;
      background: rgba(255, 255, 255, 0.6);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(231, 76, 60, 0.1);
      border-radius: 12px;

      p {
        font-size: 15px;
        color: #666;
        line-height: 1.7;
        margin: 0;
        text-indent: 2em;
      }
    }

    .merchant-images,
    .merchant-certificates {
      margin-bottom: 24px;

      .shop-image-grid,
      .cert-image-grid {
        display: flex;
        flex-direction: column;
        gap: 12px;

        img {
          width: 100%;
          height: auto;
          border-radius: 12px;
        }
      }
    }
  }
}

/* 评价列表 */
.reviews-section {
  .reviews-summary {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 24px;
    margin-bottom: 24px;
    padding: 20px 24px;
    background: linear-gradient(
      135deg,
      rgba(231, 76, 60, 0.08) 0%,
      rgba(255, 255, 255, 0.9) 100%
    );
    backdrop-filter: blur(15px);
    border: 1px solid rgba(231, 76, 60, 0.2);
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(231, 76, 60, 0.1);

    .summary-rating {
      display: flex;
      align-items: center;
      gap: 16px;

      .overall-rating {
        font-size: 28px;
        font-weight: 700;
        color: $primary-red;
        background: linear-gradient(45deg, $primary-red, $primary-red-dark);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }

    .summary-stats {
      font-size: 16px;
      color: #666;
      font-weight: 500;
    }
  }

  .reviews-list {
    display: grid;
    gap: 20px;

    .review-item {
      padding: 20px;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9) 0%,
        rgba(248, 249, 250, 0.8) 100%
      );
      backdrop-filter: blur(15px);
      border: 1px solid rgba(231, 76, 60, 0.15);
      border-radius: 12px;
      transition: all 0.3s ease;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(231, 76, 60, 0.1);
        border-color: rgba(231, 76, 60, 0.25);
      }

      .review-header {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        margin-bottom: 12px;

        .review-user {
          display: flex;
          align-items: center;
          gap: 12px;

          .user-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: linear-gradient(
              135deg,
              rgba(231, 76, 60, 0.2) 0%,
              rgba(255, 255, 255, 0.8) 100%
            );
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: 600;
            color: $primary-red;
            border: 2px solid rgba(255, 255, 255, 0.8);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .user-name {
            font-weight: 600;
            color: #2c3e50;
            font-size: 15px;
          }

          .review-rating {
            margin-left: 8px;
          }
        }

        .review-date {
          font-size: 13px;
          color: #999;
          background: rgba(255, 255, 255, 0.7);
          padding: 4px 8px;
          border-radius: 6px;
          border: 1px solid rgba(231, 76, 60, 0.1);
        }
      }

      .review-content {
        font-size: 15px;
        line-height: 1.6;
        color: #555;
        margin-bottom: 12px;
        padding: 12px 16px;
        background: rgba(255, 255, 255, 0.6);
        border-radius: 8px;
        border: 1px solid rgba(231, 76, 60, 0.1);
      }

      .review-images {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        gap: 12px;
        max-width: 400px;

        img {
          width: 100%;
          height: 80px;
          object-fit: cover;
          border-radius: 8px;
          cursor: pointer;
          border: 2px solid rgba(255, 255, 255, 0.8);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
      }
    }
  }

  .no-reviews {
    text-align: center;
    color: #999;
    padding: 60px 20px;
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 1px solid rgba(231, 76, 60, 0.1);
    font-size: 16px;
  }
}

/* 错误状态 */
.error-state {
  text-align: center;
  padding: 60px 16px;
  background: white;
  border-radius: 6px;
  color: #999;

  i {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
  }

  p {
    font-size: 14px;
    margin-bottom: 16px;
  }

  .error-btn {
    padding: 8px 16px;
    background: $primary-red;
    border: none;
    border-radius: 3px;
    color: white;
    cursor: pointer;
    transition: background 0.3s ease;
    font-size: 13px;

    &:hover {
      background: $primary-red-dark;
    }
  }
}

/* 动画效果 */
@keyframes loading {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

::v-deep(.qr-code-dialog) {
  .el-dialog {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    min-width: 320px;
    max-width: 400px;
  }

  .el-dialog__header {
    background: #e74c3c;
    color: white;
    padding: 16px 16px;

    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: white;
    }

    .el-dialog__close {
      color: white;
      font-size: 18px;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }

  .el-dialog__body {
    padding: 0;
    background: #ffffff;
  }

  .el-dialog__footer {
    background: #f8f9fa;
    border-top: 1px solid #eee;
    padding: 16px 24px;
    text-align: center;
  }
}

.qr-code-content {
  text-align: center;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;

  .qr-code-header {
    margin-bottom: 28px;
    width: 100%;
    max-width: 280px;

    h3 {
      color: #333;
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 12px 0;
      line-height: 1.4;
    }

    p {
      color: #666;
      font-size: 14px;
      line-height: 1.6;
      margin: 0;
    }
  }

  .qr-code-main {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;

    .qr-code-container {
      display: flex;
      justify-content: center;
      align-items: center;

      .qr-code-image {
        width: 200px;
        height: 200px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        background: white;
        object-fit: contain;
        display: block;
        border: 1px solid #f0f0f0;
      }
    }
  }
}

.dialog-footer {
  .el-button {
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 500;

    &.el-button--primary {
      background: linear-gradient(
        135deg,
        $primary-red 0%,
        $primary-red-dark 100%
      );
      border: none;

      &:hover {
        background: linear-gradient(
          135deg,
          $primary-red-dark 0%,
          darken($primary-red-dark, 10%) 100%
        );
      }
    }
  }
}

// 针对联系信息的特殊样式优化
::v-deep .contact-form {
  .el-input {
    .el-input__inner {
      height: 36px;
      font-size: 13px;
      border-radius: 6px;
      border: 1px solid rgba(231, 76, 60, 0.2);
      padding: 0 12px 0 35px;
      transition: all 0.2s ease;

      &:focus {
        border-color: $primary-red;
        box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.1);
      }

      &::placeholder {
        color: #c0c4cc;
        font-size: 12px;
      }
    }

    .el-input__prefix {
      left: 8px;

      .el-input__icon {
        color: rgba(231, 76, 60, 0.6);
        font-size: 14px;
      }
    }
  }
}

// 表单标签的特殊样式
::v-deep .appointment-form .form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 6px;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 5px;

  i {
    color: $primary-red;
    font-size: 14px;
  }
}
</style>
